/**
 * @file Attachment model compatibility helpers.
 */

const ATTACHMENT_CATEGORY_EXTENSION_MAP = {
    image: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    pdf: ['.pdf'],
    word: ['.doc', '.docx', '.docm', '.dot', '.dotx', '.dotm'],
    spreadsheet: ['.xls', '.xlsx', '.xlsm', '.xlt', '.xltx', '.xltm', '.csv'],
    presentation: ['.ppt', '.pptx', '.pptm', '.pot', '.potx', '.potm'],
    text: ['.txt', '.md'],
    rtf: ['.rtf'],
    archive: ['.zip'],
    email: ['.eml', '.msg'],
};

const ATTACHMENT_CATEGORY_MIME_PREFIX_MAP = {
    image: ['image/'],
};

const ATTACHMENT_CATEGORY_MIME_MAP = {
    pdf: ['application/pdf'],
    word: [
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-word.document.macroenabled.12',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
        'application/vnd.ms-word.template.macroenabled.12',
    ],
    spreadsheet: [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel.sheet.macroenabled.12',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.template',
        'application/vnd.ms-excel.template.macroenabled.12',
        'text/csv',
        'application/csv',
    ],
    text: ['text/plain', 'text/markdown', 'text/x-markdown'],
    rtf: ['application/rtf', 'text/rtf', 'application/x-rtf'],
    archive: [
        'application/zip',
        'application/x-zip-compressed',
        'multipart/x-zip',
    ],
    presentation: [
        'application/vnd.ms-powerpoint',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'application/vnd.ms-powerpoint.presentation.macroenabled.12',
        'application/vnd.openxmlformats-officedocument.presentationml.template',
        'application/vnd.ms-powerpoint.template.macroenabled.12',
    ],
    email: ['message/rfc822', 'application/vnd.ms-outlook'],
};

const OFFICE_PREVIEW_MIME_TYPES = new Set([
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);

const OFFICE_PREVIEW_EXTENSIONS = new Set([
    '.doc', '.docx', '.dotx',
    '.xls', '.xlsx',
    '.ppt', '.pptx',
]);

/**
 * Returns a stable compatibility id for legacy attachment objects.
 * @param {string} prefix Id prefix.
 * @param {string} value Source value.
 * @returns {string} Stable id.
 */
function getCompatibilityId(prefix, value) {
    const source = String(value || 'attachment')
        .replace(/[^a-zA-Z0-9_-]/g, '_')
        .replace(/_{2,}/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 80);
    return `${prefix}_${source || 'attachment'}`;
}

/**
 * Returns the lowercase extension from an attachment name or path.
 * @param {object} file Attachment file metadata.
 * @returns {string} Lowercase extension including the dot.
 */
export function getAttachmentExtension(file = {}) {
    const source = String(file.name || file.path || '').toLowerCase();
    const lastDot = source.lastIndexOf('.');
    return lastDot >= 0 ? source.slice(lastDot) : '';
}

/**
 * Returns the broad file category used by version compatibility checks.
 * @param {object} file Attachment file metadata.
 * @returns {string} Broad category id.
 */
export function getAttachmentFileCategory(file = {}) {
    const type = String(file.type || '').toLowerCase().split(';')[0].trim();
    const extension = getAttachmentExtension(file);

    for (const [category, prefixes] of Object.entries(ATTACHMENT_CATEGORY_MIME_PREFIX_MAP)) {
        if (prefixes.some((prefix) => type.startsWith(prefix))) {
            return category;
        }
    }

    for (const [category, mimeTypes] of Object.entries(ATTACHMENT_CATEGORY_MIME_MAP)) {
        if (mimeTypes.includes(type)) {
            return category;
        }
    }

    for (const [category, extensions] of Object.entries(ATTACHMENT_CATEGORY_EXTENSION_MAP)) {
        if (extensions.includes(extension)) {
            return category;
        }
    }

    return 'other';
}

/**
 * Returns whether the file belongs to a Microsoft Office preview category.
 * @param {object} file Attachment file metadata.
 * @returns {boolean} True when Office preview may be attempted.
 */
export function isOfficeAttachmentFile(file = {}) {
    const type = String(file.type || '').toLowerCase().split(';')[0].trim();
    const extension = getAttachmentExtension(file);
    return OFFICE_PREVIEW_MIME_TYPES.has(type) ||
        OFFICE_PREVIEW_EXTENSIONS.has(extension);
}

/**
 * Returns whether the file can use native image preview.
 * @param {object} file Attachment file metadata.
 * @returns {boolean} True when the file is an image.
 */
export function isImageAttachmentFile(file = {}) {
    return String(file.type || '').startsWith('image/') ||
        ATTACHMENT_CATEGORY_EXTENSION_MAP.image.includes(getAttachmentExtension(file));
}

/**
 * Returns whether the file can use native PDF preview.
 * @param {object} file Attachment file metadata.
 * @returns {boolean} True when the file is a PDF.
 */
export function isPdfAttachmentFile(file = {}) {
    return String(file.type || '').toLowerCase() === 'application/pdf' ||
        getAttachmentExtension(file) === '.pdf';
}

/**
 * Returns whether the file can use native CSV preview.
 * @param {object} file Attachment file metadata.
 * @returns {boolean} True when the file is CSV.
 */
export function isCsvAttachmentFile(file = {}) {
    const type = String(file.type || '').toLowerCase().split(';')[0].trim();
    return ['text/csv', 'application/csv'].includes(type) ||
        getAttachmentExtension(file) === '.csv';
}

/**
 * Returns whether the file can use native plain-text preview.
 * @param {object} file Attachment file metadata.
 * @returns {boolean} True when the file is plain text.
 */
export function isPlainTextAttachmentFile(file = {}) {
    return getAttachmentExtension(file) === '.txt';
}

/**
 * Returns whether the file can use native Markdown preview.
 * @param {object} file Attachment file metadata.
 * @returns {boolean} True when the file is Markdown.
 */
export function isMarkdownAttachmentFile(file = {}) {
    return getAttachmentExtension(file) === '.md';
}

/**
 * Returns whether the current attachment version has an in-app preview/open path.
 * @param {object} file Attachment file metadata.
 * @returns {boolean} True when the open action should be shown.
 */
export function canPreviewAttachmentFile(file = {}) {
    return isImageAttachmentFile(file) ||
        isPdfAttachmentFile(file) ||
        isCsvAttachmentFile(file) ||
        isMarkdownAttachmentFile(file) ||
        isPlainTextAttachmentFile(file) ||
        isOfficeAttachmentFile(file);
}

/**
 * Returns a version metadata object for a newly uploaded file.
 * @param {object} file Uploaded file metadata.
 * @param {string} file.name Stored filename.
 * @param {string} file.url Download URL.
 * @param {string} file.path Storage path.
 * @param {number} file.size File size in bytes.
 * @param {string} file.type MIME type.
 * @param {string} uploadedBy Uploading user id.
 * @returns {object} Attachment version metadata.
 */
export function createAttachmentVersionFromUpload(file, uploadedBy) {
    const versionId = crypto.randomUUID();
    return {
        id: versionId,
        name: file.name,
        url: file.url,
        path: file.path,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString(),
        uploadedBy: uploadedBy || 'unknown',
    };
}

/**
 * Returns the logical attachment shape for a newly uploaded file.
 * @param {object} file Uploaded file metadata.
 * @param {string} uploadedBy Uploading user id.
 * @returns {object} Logical attachment metadata.
 */
export function createAttachmentRecordFromUpload(file, uploadedBy) {
    const attachmentId = crypto.randomUUID();
    const version = createAttachmentVersionFromUpload(file, uploadedBy);
    return {
        id: attachmentId,
        displayName: file.name || 'Attachment',
        description: '',
        currentVersionId: version.id,
        createdAt: version.uploadedAt,
        createdBy: version.uploadedBy,
        editedAt: null,
        editedBy: null,
        versions: [version],
    };
}

/**
 * Returns the current version for a logical or legacy attachment.
 * @param {object} attachment Stored attachment metadata.
 * @returns {object|null} Current version metadata.
 */
export function getAttachmentCurrentVersion(attachment = {}) {
    if (Array.isArray(attachment.versions)) {
        return attachment.versions.find((version) => {
            return version?.id === attachment.currentVersionId;
        }) || attachment.versions[0] || null;
    }

    return attachment?.path ? attachment : null;
}

/**
 * Normalizes a legacy flat attachment or upgraded logical attachment.
 * @param {object} attachment Stored attachment metadata.
 * @param {number} index Attachment index fallback.
 * @returns {object|null} Normalized attachment.
 */
export function normalizeAttachmentRecord(attachment = {}, index = 0) {
    if (!attachment || typeof attachment !== 'object') {
        return null;
    }

    if (Array.isArray(attachment.versions)) {
        const currentVersion = getAttachmentCurrentVersion(attachment);
        if (!currentVersion) {
            return null;
        }

        return {
            ...attachment,
            id: attachment.id || getCompatibilityId('att', currentVersion.path || index),
            displayName: attachment.displayName || currentVersion.name || 'Attachment',
            description: attachment.description || '',
            currentVersionId: attachment.currentVersionId || currentVersion.id,
            currentVersion,
            category: getAttachmentFileCategory(currentVersion),
            isLegacy: false,
        };
    }

    if (!attachment.path) {
        return null;
    }

    const versionId = getCompatibilityId('ver', attachment.path || index);
    return {
        id: getCompatibilityId('att', attachment.path || index),
        displayName: attachment.name || 'Attachment',
        description: '',
        currentVersionId: versionId,
        createdAt: attachment.uploadedAt || null,
        createdBy: attachment.uploadedBy || null,
        editedAt: null,
        editedBy: null,
        versions: [{
            id: versionId,
            name: attachment.name || 'Attachment',
            url: attachment.url || '',
            path: attachment.path,
            size: attachment.size || null,
            type: attachment.type || '',
            uploadedAt: attachment.uploadedAt || null,
            uploadedBy: attachment.uploadedBy || null,
        }],
        currentVersion: attachment,
        category: getAttachmentFileCategory(attachment),
        isLegacy: true,
    };
}

/**
 * Normalizes an attachment array while dropping unusable records.
 * @param {Array<object>} attachments Stored attachment array.
 * @returns {Array<object>} Normalized attachments.
 */
export function normalizeAttachmentRecords(attachments = []) {
    if (!Array.isArray(attachments)) {
        return [];
    }

    return attachments
        .map((attachment, index) => normalizeAttachmentRecord(attachment, index))
        .filter(Boolean);
}

/**
 * Finds a normalized attachment by its current version path.
 * @param {Array<object>} attachments Stored attachment array.
 * @param {string} filePath Storage path.
 * @returns {object|null} Normalized attachment.
 */
export function findAttachmentByCurrentPath(attachments = [], filePath) {
    return normalizeAttachmentRecords(attachments).find((attachment) => {
        return attachment.currentVersion?.path === filePath;
    }) || null;
}
